import { useState, useCallback, useEffect } from 'react';
import {
  fetchEmployeeHolidaySavingsAmount,
  EmployeeHolidaySavingsRecord,
  HolidaySavingsResponse,
} from '../api/salary/holiday-savings.service';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface UseHolidaySavingsAmountResult {
  fetchHolidaySavingsAmount: (
    fromDate?: string,
    toDate?: string
  ) => Promise<void>;
  holidaySavingsRecords: EmployeeHolidaySavingsRecord[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  setCurrentPage: (page: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  refetch: () => Promise<void>;
  appliedFilters: {
    fromDate: string;
    toDate: string;
  };
}

export const useHolidaySavingsAmount = (
  pageSize: number = 10
): UseHolidaySavingsAmountResult => {
  const [holidaySavingsRecords, setHolidaySavingsRecords] = useState<
    EmployeeHolidaySavingsRecord[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: '',
    toDate: '',
  });
  const { t } = useTranslation();

  const fetchHolidaySavingsAmount = useCallback(
    async (fromDate?: string, toDate?: string) => {
      try {
        setLoading(true);
        setError(null);

        const response: HolidaySavingsResponse =
          await fetchEmployeeHolidaySavingsAmount({
            PageNumber: currentPage,
            PageSize: pageSize,
            fromDate,
            toDate,
          });

        if (response && response.response) {
          setHolidaySavingsRecords(response.response);
          if (response.numberOfPages) {
            setTotalPages(response.numberOfPages);
          }
          setAppliedFilters({
            fromDate: fromDate || '',
            toDate: toDate || '',
          });
        }
      } catch (err) {
        console.error('Failed to fetch holiday savings amount:', err);
        const errorMessage =
          (err as Error).message || 'Failed to load holiday savings amount';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize]
  );

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  const refetch = useCallback(async () => {
    await fetchHolidaySavingsAmount(
      appliedFilters.fromDate,
      appliedFilters.toDate
    );
  }, [fetchHolidaySavingsAmount, appliedFilters]);

  return {
    fetchHolidaySavingsAmount,
    holidaySavingsRecords,
    loading,
    error,
    currentPage,
    totalPages,
    pageSize,
    setCurrentPage,
    goToNextPage,
    goToPreviousPage,
    refetch,
    appliedFilters,
  };
};
